import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Slider,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import {
  Search as SearchIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { apiClient } from '../utils/api';

interface DuplicateResult {
  case_id: number;
  score: number;
  score_details: {
    patient_score: number;
    product_score: number;
    event_score: number;
    reporter_score: number;
    date_score: number;
  };
  case_info: {
    id: number;
    description: string;
    severity: string;
    event_date: string;
    patient_age: number;
    patient_gender: string;
    product_name: string;
    reported_by: string;
    report_source: string;
    is_serious: boolean;
  };
  matched_fields: string[];
}

interface DuplicateLink {
  link_id: number;
  link_type: string;
  is_parent: boolean;
  other_case_id: number;
  other_case_description: string;
  confidence_score: number;
  linked_by: number;
  linked_at: string;
}

interface DuplicateConfig {
  auto_merge_threshold: number;
  manual_review_threshold: number;
  field_weights: {
    patient_name: number;
    patient_dob: number;
    patient_gender: number;
    product_name: number;
    event_term: number;
    reporter: number;
    date: number;
  };
  search_window_days: number;
  max_results: number;
  include_substance_search: boolean;
  store_search_results: boolean;
  use_ml_scoring: boolean;
}

interface SearchDuplicatesResponse {
  case_id: number;
  duplicates_found: number;
  results: DuplicateResult[];
}

interface LinksResponse {
  case_id: number;
  total_links: number;
  links: DuplicateLink[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DuplicateDetection: React.FC = () => {
  const [selectedCase, setSelectedCase] = useState<number | null>(null);
  const [searchResults, setSearchResults] = useState<DuplicateResult[]>([]);
  const [duplicateLinks, setDuplicateLinks] = useState<DuplicateLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    sourceId: number | null;
    targetId: number | null;
    action: 'confirm' | 'reject';
  }>({ open: false, sourceId: null, targetId: null, action: 'confirm' });
  const [notes, setNotes] = useState('');
  const [config, setConfig] = useState<DuplicateConfig | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [searchThreshold, setSearchThreshold] = useState(70);
  const [maxResults, setMaxResults] = useState(10);

  // Load configuration on mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data, error } = await apiClient.get<DuplicateConfig>('/duplicate-detection/config');
      if (error) throw new Error(error);
      if (data) {
        setConfig(data);
        setSearchThreshold(data.manual_review_threshold);
        setMaxResults(data.max_results);
      }
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const searchDuplicates = async (caseId: number) => {
    setLoading(true);
    setError(null);
    setSelectedCase(caseId);
    
    try {
      const endpoint = `/duplicate-detection/search/${caseId}?threshold=${encodeURIComponent(searchThreshold)}&max_results=${encodeURIComponent(maxResults)}`;
      const { data, error } = await apiClient.post<SearchDuplicatesResponse>(endpoint, {});
      if (error) throw new Error(error);
      if (data) {
        setSearchResults(data.results || []);
        setSuccess(`Found ${data.duplicates_found} potential duplicates`);
      }
      
      // Also load existing links
      await loadDuplicateLinks(caseId);
    } catch (err: any) {
      setError(err?.message || 'Failed to search duplicates');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicateLinks = async (caseId: number) => {
    try {
      const { data, error } = await apiClient.get<LinksResponse>(`/duplicate-detection/links/${caseId}`);
      if (error) throw new Error(error);
      setDuplicateLinks(data?.links || []);
    } catch (err) {
      console.error('Failed to load duplicate links:', err);
    }
  };

  const handleConfirmDuplicate = async () => {
    if (!confirmDialog.sourceId || !confirmDialog.targetId) return;
    
    setLoading(true);
    try {
      const endpoint = confirmDialog.action === 'confirm' 
        ? '/duplicate-detection/confirm-duplicate'
        : '/duplicate-detection/reject-duplicate';
      
      const body = {
        source_case_id: confirmDialog.sourceId,
        target_case_id: confirmDialog.targetId,
        [confirmDialog.action === 'confirm' ? 'notes' : 'reason']: notes
      };
      
      await apiClient.post(endpoint, body);
      
      setSuccess(
        confirmDialog.action === 'confirm'
          ? 'Cases linked as duplicates successfully'
          : 'Duplicate suggestion rejected'
      );
      
      // Refresh results
      if (selectedCase) {
        await searchDuplicates(selectedCase);
      }
      
      setConfirmDialog({ open: false, sourceId: null, targetId: null, action: 'confirm' });
      setNotes('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (linkId: number) => {
    if (!window.confirm('Are you sure you want to unlink these cases?')) return;
    
    setLoading(true);
    try {
      await apiClient.delete(`/duplicate-detection/links/${linkId}`);
      setSuccess('Cases unlinked successfully');
      
      if (selectedCase) {
        await loadDuplicateLinks(selectedCase);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unlink cases');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'error';
    if (score >= 70) return 'warning';
    return 'info';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 90) return <WarningIcon color="error" />;
    if (score >= 70) return <InfoIcon color="warning" />;
    return <InfoIcon color="info" />;
  };

  const loadSampleData = () => {
    const sampleConfig: DuplicateConfig = {
      auto_merge_threshold: 95,
      manual_review_threshold: 70,
      field_weights: {
        patient_name: 0.25,
        patient_dob: 0.15,
        patient_gender: 0.1,
        product_name: 0.25,
        event_term: 0.15,
        reporter: 0.05,
        date: 0.05,
      },
      search_window_days: 30,
      max_results: 10,
      include_substance_search: true,
      store_search_results: false,
      use_ml_scoring: false,
    };

    if (!config) {
      setConfig(sampleConfig);
      setSearchThreshold(sampleConfig.manual_review_threshold);
      setMaxResults(sampleConfig.max_results);
    }

    setSelectedCase(1001);
    const sampleResults: DuplicateResult[] = [
      {
        case_id: 2002,
        score: 86.4,
        score_details: {
          patient_score: 0.82,
          product_score: 0.9,
          event_score: 0.78,
          reporter_score: 0.7,
          date_score: 0.88,
        },
        case_info: {
          id: 2002,
          description: 'Rash and redness after applying night serum',
          severity: 'Moderate',
          event_date: new Date().toISOString(),
          patient_age: 34,
          patient_gender: 'F',
          product_name: 'Radiance Night Serum',
          reported_by: 'Dermatologist',
          report_source: 'Clinic',
          is_serious: false,
        },
        matched_fields: ['product_name', 'event_term', 'date'],
      },
      {
        case_id: 2005,
        score: 72.1,
        score_details: {
          patient_score: 0.6,
          product_score: 0.8,
          event_score: 0.65,
          reporter_score: 0.5,
          date_score: 0.7,
        },
        case_info: {
          id: 2005,
          description: 'Mild irritation reported after lotion use',
          severity: 'Mild',
          event_date: new Date(Date.now() - 3 * 86400000).toISOString(),
          patient_age: 35,
          patient_gender: 'F',
          product_name: 'Radiance Night Serum',
          reported_by: 'Consumer',
          report_source: 'Web',
          is_serious: false,
        },
        matched_fields: ['product_name', 'patient_gender'],
      },
    ];
    setSearchResults(sampleResults);
    setSuccess(`Loaded ${sampleResults.length} sample results`);
  };

  const renderSearchTab = () => (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Case ID"
              type="number"
              placeholder="Enter case ID to search"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const value = (e.target as HTMLInputElement).value;
                  if (value) searchDuplicates(parseInt(value));
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box>
              <Typography gutterBottom>Threshold: {searchThreshold}%</Typography>
              <Slider
                value={searchThreshold}
                onChange={(_, value) => setSearchThreshold(value as number)}
                min={50}
                max={100}
                valueLabelDisplay="auto"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box>
              <Typography gutterBottom>Max Results: {maxResults}</Typography>
              <Slider
                value={maxResults}
                onChange={(_, value) => setMaxResults(value as number)}
                min={5}
                max={50}
                valueLabelDisplay="auto"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => {
                const input = document.querySelector('input[type="number"]') as HTMLInputElement;
                if (input?.value) searchDuplicates(parseInt(input.value));
              }}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<CompareArrowsIcon />} onClick={loadSampleData}>
          Load Sample Data
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {searchResults.length > 0 && (
        <Grid container spacing={2}>
          {searchResults.map((result) => (
            <Grid item xs={12} md={6} key={result.case_id}>
              <Card elevation={3}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Case #{result.case_id}
                    </Typography>
                    <Chip
                      label={`${result.score.toFixed(1)}% Match`}
                      color={getScoreColor(result.score)}
                      icon={getScoreIcon(result.score)}
                    />
                  </Box>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    {result.case_info.description}
                  </Typography>

                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Patient
                      </Typography>
                      <Typography variant="body2">
                        {result.case_info.patient_age}y / {result.case_info.patient_gender}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Product
                      </Typography>
                      <Typography variant="body2">
                        {result.case_info.product_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Event Date
                      </Typography>
                      <Typography variant="body2">
                        {new Date(result.case_info.event_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Severity
                      </Typography>
                      <Typography variant="body2">
                        {result.case_info.severity}
                        {result.case_info.is_serious && (
                          <Chip size="small" label="SAE" color="error" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">Score Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption">Patient: {(result.score_details.patient_score * 100).toFixed(0)}%</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">Product: {(result.score_details.product_score * 100).toFixed(0)}%</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">Event: {(result.score_details.event_score * 100).toFixed(0)}%</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">Reporter: {(result.score_details.reporter_score * 100).toFixed(0)}%</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption">Date: {(result.score_details.date_score * 100).toFixed(0)}%</Typography>
                        </Grid>
                      </Grid>
                      
                      {result.matched_fields.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="caption" color="textSecondary">
                            Matched Fields:
                          </Typography>
                          <Box mt={1}>
                            {result.matched_fields.map((field) => (
                              <Chip
                                key={field}
                                label={field}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => setConfirmDialog({
                      open: true,
                      sourceId: selectedCase,
                      targetId: result.case_id,
                      action: 'confirm'
                    })}
                  >
                    Confirm Duplicate
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => setConfirmDialog({
                      open: true,
                      sourceId: selectedCase,
                      targetId: result.case_id,
                      action: 'reject'
                    })}
                  >
                    Not Duplicate
                  </Button>
                  <IconButton size="small">
                    <VisibilityIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {searchResults.length === 0 && selectedCase && !loading && (
        <Alert severity="info">
          No potential duplicates found for case #{selectedCase}
        </Alert>
      )}
    </Box>
  );

  const renderLinksTab = () => (
    <Box>
      {selectedCase && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing duplicate links for case #{selectedCase}
        </Alert>
      )}

      {duplicateLinks.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Linked Case</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Relationship</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Linked By</TableCell>
                <TableCell>Linked At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {duplicateLinks.map((link) => (
                <TableRow key={link.link_id}>
                  <TableCell>#{link.other_case_id}</TableCell>
                  <TableCell>
                    <Chip
                      label={link.link_type}
                      size="small"
                      color={link.link_type === 'duplicate' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {link.is_parent ? 'Child of' : 'Parent of'}
                  </TableCell>
                  <TableCell>{link.other_case_description}</TableCell>
                  <TableCell>User #{link.linked_by}</TableCell>
                  <TableCell>
                    {new Date(link.linked_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleUnlink(link.link_id)}
                    >
                      <LinkOffIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          {selectedCase 
            ? `No duplicate links found for case #${selectedCase}`
            : 'Search for a case to view its duplicate links'}
        </Alert>
      )}
    </Box>
  );

  const renderConfigTab = () => (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Duplicate Detection Configuration
        </Typography>

        {!config && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Configuration not loaded. Load a sample to preview this tab.
            </Alert>
            <Button variant="outlined" onClick={loadSampleData}>
              Load Sample Config
            </Button>
          </>
        )}

        {config && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Thresholds
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">Auto-merge: {config.auto_merge_threshold}%</Typography>
                  <Typography variant="body2">Manual Review: {config.manual_review_threshold}%</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Search Parameters
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">Search Window: {config.search_window_days} days</Typography>
                  <Typography variant="body2">Max Results: {config.max_results}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Field Weights
                </Typography>
                <Grid container spacing={2} sx={{ pl: 2 }}>
                  {Object.entries(config.field_weights).map(([field, weight]) => (
                    <Grid item xs={6} md={3} key={field}>
                      <Typography variant="body2">
                        {field.replace('_', ' ')}: {(weight * 100).toFixed(0)}%
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Options
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">Include Substance Search: {config.include_substance_search ? 'Yes' : 'No'}</Typography>
                  <Typography variant="body2">Store Search Results: {config.store_search_results ? 'Yes' : 'No'}</Typography>
                  <Typography variant="body2">Use ML Scoring: {config.use_ml_scoring ? 'Yes' : 'No'}</Typography>
                </Box>
              </Grid>
            </Grid>

            <Box mt={3}>
              <Button variant="contained" startIcon={<SettingsIcon />} onClick={() => setConfigDialogOpen(true)}>
                Edit Configuration
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Duplicate Detection System
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Search and manage duplicate cases using intelligent matching algorithms
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mx: 2, mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)}>
          <Tab label="Search Duplicates" icon={<SearchIcon />} />
          <Tab label="Duplicate Links" icon={<LinkIcon />} />
          <Tab label="Configuration" icon={<SettingsIcon />} />
        </Tabs>
      </Paper>
      
      <TabPanel value={tabValue} index={0}>
        {renderSearchTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        {renderLinksTab()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        {renderConfigTab()}
      </TabPanel>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, sourceId: null, targetId: null, action: 'confirm' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'confirm' ? 'Confirm Duplicate' : 'Reject Duplicate'}
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            {confirmDialog.action === 'confirm'
              ? `Are you sure you want to link case #${confirmDialog.sourceId} and case #${confirmDialog.targetId} as duplicates?`
              : `Are you sure these cases are NOT duplicates?`}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={confirmDialog.action === 'confirm' ? 'Notes (optional)' : 'Reason (optional)'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, sourceId: null, targetId: null, action: 'confirm' })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDuplicate}
            color={confirmDialog.action === 'confirm' ? 'success' : 'error'}
            variant="contained"
            disabled={loading}
          >
            {confirmDialog.action === 'confirm' ? 'Confirm' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuplicateDetection;
